
import { useState, useEffect } from "react";
import { Check, Filter, X } from "lucide-react";
import { getCategories, Category, getWorks, WorkItem } from "../lib/contentful";
import { Button } from "./ui/button";

export type WorkCategory = string;

interface WorkFilterProps {
  onFilterChange: (categories: WorkCategory[]) => void;
}

const WorkFilter = ({ onFilterChange }: WorkFilterProps) => {
  const [selectedFilters, setSelectedFilters] = useState<WorkCategory[]>(["featured"]);
  const [filterOptions, setFilterOptions] = useState<Category[]>([
    { id: "featured", label: "Featured" }
  ]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchCategoriesAndWorks = async () => {
      setIsLoading(true);
      try {
        // Fetch all categories and works
        const categories = await getCategories();
        const works = await getWorks();
        
        // Get all unique category IDs that appear in works
        const usedCategoryIds = new Set<string>();
        works.forEach(work => {
          work.categories.forEach(category => {
            usedCategoryIds.add(category);
          });
        });
        
        // Filter categories to only show ones that have associated works
        // Always include "featured" category regardless
        const filteredCategories = categories.filter(cat => 
          cat.id === "featured" || usedCategoryIds.has(cat.id)
        );
        
        // Create a new array with "featured" first, then the rest of the categories
        // excluding "featured" if it already exists in the fetched categories
        const sortedCategories = [
          { id: "featured", label: "Featured" },
          ...filteredCategories.filter(cat => cat.id !== "featured")
        ];
        
        setFilterOptions(sortedCategories);
      } catch (error) {
        console.error("Error fetching categories or works:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCategoriesAndWorks();
  }, []);

  const handleFilterToggle = (filter: WorkCategory) => {
    let newFilters: WorkCategory[];

    if (filter === "featured") {
      // If featured is clicked, deselect all others and select only featured
      newFilters = selectedFilters.includes("featured") ? [] : ["featured"];
    } else {
      // If any other filter is clicked, remove featured and toggle this filter
      newFilters = selectedFilters.filter(f => f !== "featured");
      
      if (selectedFilters.includes(filter)) {
        // Remove this filter if already selected
        newFilters = newFilters.filter(f => f !== filter);
      } else {
        // Add this filter
        newFilters.push(filter);
      }
      
      // If no filters selected, default back to featured
      if (newFilters.length === 0) {
        newFilters = ["featured"];
      }
    }

    setSelectedFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearFilters = () => {
    setSelectedFilters(["featured"]);
    onFilterChange(["featured"]);
  };

  const showClearButton = selectedFilters.length > 0 && !selectedFilters.includes("featured");

  return (
    <div className="mb-10 w-full">
      <div className="flex flex-col items-center justify-center gap-3">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Filter by category</span>
          {showClearButton && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 px-2 ml-2 text-muted-foreground hover:text-foreground flex items-center gap-1" 
              onClick={handleClearFilters}
            >
              <X className="h-3.5 w-3.5" />
              <span className="text-xs">Clear</span>
            </Button>
          )}
        </div>
        
        <div className="flex flex-wrap items-center justify-center gap-3">
          {filterOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => handleFilterToggle(option.id)}
              className={`
                px-4 py-2 rounded-full text-sm transition-all duration-300 flex items-center gap-2
                ${
                  selectedFilters.includes(option.id)
                    ? "bg-primary text-primary-foreground scale-105 after:ring-2"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:scale-105 hover:shadow-md relative group"
                }
                group relative overflow-hidden
              `}
            >
              {selectedFilters.includes(option.id) && (
                <Check className="w-4 h-4" />
              )}
              {option.label}
              <span className={`
                absolute inset-0 rounded-full border border-transparent 
                transition-all duration-300 
                ${selectedFilters.includes(option.id) ? 'border-primary/50' : 'group-hover:border-primary/30'}
              `}></span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorkFilter;
